import type {
  PogCsvDataset,
  ResponseGetMerchflowsExportsCsvColumns,
} from "@CommonApi/merchflows/exports/csv/columns";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "src/api";
import { useModals } from "src/components/Modals";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  InlineMessage,
  Modal,
  Scroller,
  Search,
  Tabs,
  Text,
} from "src/elements";
import type { CheckboxProps } from "src/elements/Checkbox/Checkbox";
import { useTranslation } from "src/i18n";
import { pogCsvColumnsModalDataAtom } from "./store/atoms";

type Column = ResponseGetMerchflowsExportsCsvColumns["columns"][number];
type DatasetState = {
  metadata: ResponseGetMerchflowsExportsCsvColumns | null;
  selected: Set<string>;
  search: string;
  status: "idle" | "loading" | "loaded" | "failed";
};

const createDatasetState = (): DatasetState => ({
  metadata: null,
  selected: new Set(),
  search: "",
  status: "idle",
});

export const PogCsvColumnsModal = () => {
  const [data, setData] = useAtom(pogCsvColumnsModalDataAtom);

  useEffect(
    () => () => {
      setData(null);
    },
    [setData],
  );

  if (!data) return null;

  return <PogCsvColumnsModalInner />;
};

const PogCsvColumnsModalInner = () => {
  const [data] = useAtom(pogCsvColumnsModalDataAtom);
  const { closeModal } = useModals();
  const { t } = useTranslation("common");
  const { getMerchflowsExportsCsvColumnsApi, postExportBatchPogsApi } = useApi();
  const [csvDataset, setCsvDataset] = useState<PogCsvDataset>("ranged");
  const [datasetStates, setDatasetStates] = useState<Record<PogCsvDataset, DatasetState>>(() => ({
    ranged: createDatasetState(),
    unranged: createDatasetState(),
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setDatasetState = (
    dataset: PogCsvDataset,
    update: (state: DatasetState) => DatasetState,
  ) => {
    setDatasetStates((current) => ({ ...current, [dataset]: update(current[dataset]) }));
  };

  const loadColumns = async (dataset: PogCsvDataset) => {
    setDatasetState(dataset, (state) => ({ ...state, status: "loading" }));
    const response = await getMerchflowsExportsCsvColumnsApi({ csvDataset: dataset });

    if (!response) {
      setDatasetState(dataset, (state) => ({ ...state, metadata: null, status: "failed" }));
      return;
    }

    setDatasetState(dataset, (state) => ({
      ...state,
      metadata: response,
      selected: new Set(response.selectedColumns),
      status: "loaded",
    }));
  };

  useEffect(() => {
    loadColumns("ranged");
  }, []);

  const datasetState = datasetStates[csvDataset];
  const { metadata, search, selected } = datasetState;
  const isLoading = datasetState.status === "idle" || datasetState.status === "loading";
  const loadFailed = datasetState.status === "failed";

  const changeDataset = (dataset: PogCsvDataset) => {
    setCsvDataset(dataset);
    if (datasetStates[dataset].status === "idle") loadColumns(dataset);
  };

  const visibleColumns = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return metadata?.columns ?? [];
    return (metadata?.columns ?? []).filter((column) =>
      `${column.label} ${column.key} ${column.group}`.toLowerCase().includes(query),
    );
  }, [metadata, search]);

  const visibleGroups = useMemo(() => {
    const groups = new Map<string, Column[]>();
    for (const column of visibleColumns) {
      groups.set(column.group, [...(groups.get(column.group) ?? []), column]);
    }
    return groups;
  }, [visibleColumns]);

  const setColumnSelected = (key: string, isChecked: boolean) => {
    setDatasetState(csvDataset, (state) => {
      const next = new Set(state.selected);
      if (isChecked) next.add(key);
      else next.delete(key);
      return { ...state, selected: next };
    });
  };

  const setGroupSelected = (group: string, isChecked: boolean) => {
    const groupColumns = metadata?.columns.filter((column) => column.group === group) ?? [];
    setDatasetState(csvDataset, (state) => {
      const next = new Set(state.selected);
      for (const column of groupColumns) {
        if (isChecked) next.add(column.key);
        else next.delete(column.key);
      }
      return { ...state, selected: next };
    });
  };

  const exportCsv = async () => {
    if (!data || !metadata || selected.size === 0) return;

    setIsSubmitting(true);
    const columns = metadata.columns
      .filter((column) => selected.has(column.key))
      .map((column) => column.key);
    const response = await postExportBatchPogsApi(data.merchflowId, {
      ...data.payload,
      type: "csv",
      columns,
      csvDataset,
    });
    setIsSubmitting(false);

    if (response?.status === "DONE") {
      data.onSuccess?.();
      closeModal("PogCsvColumnsModal");
    }
  };

  const total = metadata?.columns.length ?? 0;

  return (
    <Modal
      name="PogCsvColumnsModal"
      title={t("storePogExport.csvColumns.title")}
      width="600px"
      height="70vh"
      confirmModal={isSubmitting ? () => undefined : false}
    >
      <Flex column gap="16px" padding="20px" minHeight="0" flexGrow>
        <Tabs
          tabs={[
            { label: t("storePogExport.csvColumns.rangedItems"), value: "ranged" },
            { label: t("storePogExport.csvColumns.unrangedItems"), value: "unranged" },
          ]}
          value={csvDataset}
          setValue={changeDataset}
          isDisabled={isSubmitting}
        />

        {isLoading ? (
          <Text>{t("storePogExport.csvColumns.loading")}</Text>
        ) : loadFailed ? (
          <Flex column gap="12px">
            <InlineMessage status="error" width="100%">
              {t("storePogExport.csvColumns.loadFailed")}
            </InlineMessage>
            <Button onClick={() => loadColumns(csvDataset)} width="fit-content">
              {t("storePogExport.csvColumns.retry")}
            </Button>
          </Flex>
        ) : (
          <>
            <Flex align="center" justify="between" gap="12px">
              <Search
                search={search}
                setSearch={(nextSearch) =>
                  setDatasetState(csvDataset, (state) => ({ ...state, search: nextSearch }))
                }
                placeholder={t("storePogExport.csvColumns.search")}
                width="300px"
                isFocusedAutomatically
              />
              <Text variant="caption1">
                {t("storePogExport.csvColumns.selectedCount", {
                  selected: selected.size,
                  total,
                })}
              </Text>
            </Flex>

            <Scroller>
              {[...visibleGroups].map(([group, columns]) => {
                const selectedCount =
                  metadata?.columns.filter(
                    (column) => column.group === group && selected.has(column.key),
                  ).length ?? 0;
                const groupTotal =
                  metadata?.columns.filter((column) => column.group === group).length ?? 0;
                const groupChecked: CheckboxProps["isChecked"] =
                  selectedCount === 0 ? false : selectedCount === groupTotal ? true : "mixed";

                return (
                  <Flex
                    key={group}
                    column
                    gap="8px"
                    padding="10px 4px"
                    borderBottom={`1px solid ${Color.gray200}`}
                  >
                    <Checkbox
                      name={`csv-group-${group}`}
                      label={group}
                      isChecked={groupChecked}
                      setIsChecked={(isChecked) => setGroupSelected(group, isChecked)}
                      isHeader
                      isCheckboxLeft
                      role="checkbox"
                    />
                    <Flex column gap="8px" padding="0 0 0 24px">
                      {columns.map((column) => (
                        <Checkbox
                          key={column.key}
                          name={`csv-column-${column.key}`}
                          label={column.label}
                          isChecked={selected.has(column.key)}
                          setIsChecked={(isChecked) => setColumnSelected(column.key, isChecked)}
                          isCheckboxLeft
                          role="checkbox"
                        />
                      ))}
                    </Flex>
                  </Flex>
                );
              })}
              {visibleGroups.size === 0 && <Text>{t("storePogExport.csvColumns.noMatches")}</Text>}
            </Scroller>
          </>
        )}

        <Flex justify="right" gap="10px" margin="auto 0 0">
          <Button
            variant="inverted"
            isDisabled={isSubmitting}
            onClick={() => closeModal("PogCsvColumnsModal")}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={exportCsv}
            isDisabled={!metadata || selected.size === 0 || isLoading || loadFailed}
            isLoading={isSubmitting}
          >
            {t("storePogExport.csvColumns.export")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
